import React from "react";
import "@/App.css";
import "@/lib/theme"; // self-applies stored theme preference on load
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";

import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import ForSellers from "@/pages/ForSellers";
import ForInvestors from "@/pages/ForInvestors";
import ForProfessionals from "@/pages/ForProfessionals";
import Pricing from "@/pages/Pricing";
import Download from "@/pages/Download";
import { Terms, Privacy, Disclaimer } from "@/pages/Legal";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelect from "@/pages/RoleSelect";

import Dashboard from "@/pages/Dashboard";
import SubmitProperty from "@/pages/SubmitProperty";
import PropertyDetail from "@/pages/PropertyDetail";
import Opportunities from "@/pages/Opportunities";
import MyOffers from "@/pages/MyOffers";
import Messages from "@/pages/Messages";
import Appointments from "@/pages/Appointments";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";

function AppRouter() {
  const location = useLocation();
  // Synchronous check — handle OAuth callback BEFORE any route renders
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public marketing */}
      <Route path="/" element={<Home />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/for-sellers" element={<ForSellers />} />
      <Route path="/for-investors" element={<ForInvestors />} />
      <Route path="/for-realtors" element={<ForProfessionals />} />
      <Route path="/for-attorneys" element={<ForProfessionals />} />
      <Route path="/for-contractors" element={<ForProfessionals />} />
      <Route path="/for-professionals" element={<ForProfessionals />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/download" element={<Download />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/disclaimer" element={<Disclaimer />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/select-role" element={<RoleSelect />} />

      {/* App (protected) */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/submit-property" element={<ProtectedRoute roles={["seller"]}><SubmitProperty /></ProtectedRoute>} />
      <Route path="/properties/:pid" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />
      <Route path="/opportunities" element={<ProtectedRoute roles={["investor","realtor","attorney","lender","contractor","title","admin"]}><Opportunities /></ProtectedRoute>} />
      <Route path="/my-offers" element={<ProtectedRoute roles={["investor","realtor","attorney","lender","contractor","title"]}><MyOffers /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/providers" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/properties" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/offers" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/conversations" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/appointments" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/danger" element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />

      <Route path="*" element={<Home />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <InstallPrompt />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
